/**
 * Centralized role + capability resolution for AriaOne (frontend).
 *
 * Authoritative sources, in priority order:
 *   1. `user.permissions` — server-computed capability map from
 *      `/api/user-access/me` (POLICY in /app/backend/rbac.py).
 *      When present, used verbatim — never second-guessed by the
 *      local role-map.
 *   2. `user.app_role` — per-org app role from
 *      `/api/user-access/me`.
 *   3. **Team-context dispatch via `user.user_type`** (AriaOne §14):
 *        - `user_type === 'non_medical' | 'non-medical' | 'nonMedical'`
 *          → use `user.non_medical_role` (front_desk / office_manager /
 *            biller / scheduling_coordinator / …).
 *        - `user_type === 'medical_provider' | 'medical' | …`
 *          → use `user.member_role || user.role`.
 *      The vendor team API splits operational role from clinical role
 *      explicitly; this step honors that split when the caller carries
 *      a team-context object.
 *   4. `user.role` — coarse token role from `/api/auth/me`.
 *   5. `user.non_medical_role` — operational role attached to
 *      team members whose `category === 'nonMedical'`. Forwarded
 *      to the role-alias table (e.g. `front_desk`, `office_manager`,
 *      `biller`).
 *   6. `user.category === 'medical'` — when the user is a medical
 *      team member with no role assigned, default to `provider`.
 *
 * Unknown / unaliased roles → `'read_only'` (least-privilege known
 * role, gives read access to scheduler/summary but never write).
 * The raw value is reported via `unmappedRoleTelemetry` so we can
 * track new vendor roles that need an alias.
 *
 * `/api/auth/permissions` IS OPTIONAL — see `fetchAuthPermissionsSafe`
 * below. Vendor-hosted environments may not expose this endpoint and
 * will 404; downstream code must never block on it.
 *
 * Mirror of the backend interim RBAC policy in /app/backend/rbac.py
 * — keep this file in sync with that module + /app/RBAC_INTERIM.md.
 */

// Keep identical to backend `_ROLE_ALIASES`, plus extended coverage
// of vendor role strings. Vendor staff roles (office_manager,
// scheduling_coordinator, biller, …) all map to `front_desk` so they
// retain `scheduler:ops:write`.
const ROLE_ALIASES = {
  '': 'client',
  'user': 'client',
  'client': 'client',
  'client/user': 'client',
  'read': 'read_only',
  'readonly': 'read_only',
  'read-only': 'read_only',
  'read_only': 'read_only',
  'guest': 'read_only',
  'viewer': 'read_only',
  'provider': 'provider',
  'physician': 'provider',
  'np': 'provider',
  'pa': 'provider',
  'doctor': 'provider',
  'clinician': 'provider',
  'md': 'provider',
  'do': 'provider',
  'front_desk': 'front_desk',
  'frontdesk': 'front_desk',
  'front-desk': 'front_desk',
  'receptionist': 'front_desk',
  'scheduler': 'front_desk',
  'scheduling_coordinator': 'front_desk',
  'scheduling-coordinator': 'front_desk',
  'intake': 'front_desk',
  'intake_coordinator': 'front_desk',
  'biller': 'front_desk',
  'billing': 'front_desk',
  'biller/coder': 'front_desk',
  'coder': 'front_desk',
  'office_manager': 'front_desk',
  'office-manager': 'front_desk',
  'practice_admin': 'admin',
  'practice-admin': 'admin',
  'practice_administrator': 'admin',
  'clinic_admin': 'admin',
  'clinic-admin': 'admin',
  'medical_assistant': 'medical_assistant',
  'medical-assistant': 'medical_assistant',
  'ma': 'medical_assistant',
  'nurse': 'medical_assistant',
  'rn': 'medical_assistant',
  'registered_nurse': 'medical_assistant',
  'nursing_assistant': 'medical_assistant',
  'lpn': 'medical_assistant',
  'admin': 'admin',
  'administrator': 'admin',
  'manager': 'admin',
  'super_admin': 'super_admin',
  'superadmin': 'super_admin',
  'super-admin': 'super_admin',
  'owner': 'super_admin',
  'tenant_owner': 'super_admin',
};

export const CANONICAL_ROLES = Object.freeze([
  'client', 'read_only', 'provider', 'medical_assistant',
  'front_desk', 'admin', 'super_admin',
]);

/* Least-privilege fallback for users with a non-empty but unrecognized
 * role string. Read access only; never grants writes or admin. */
export const UNKNOWN_ROLE_FALLBACK = 'read_only';

// Keep identical to backend `POLICY`.
const POLICY = {
  'team:admin':             new Set(['super_admin', 'admin']),
  'scheduler:admin':        new Set(['super_admin', 'admin']),
  'scheduler:ops:write':    new Set(['super_admin', 'admin', 'front_desk', 'medical_assistant', 'provider']),
  'scheduler:ops:read':     new Set(['super_admin', 'admin', 'front_desk', 'medical_assistant', 'provider', 'read_only']),
  'scheduler:summary:read': new Set(['super_admin', 'admin', 'front_desk', 'medical_assistant', 'provider', 'read_only']),
};

/* ─────────── Unmapped-role telemetry ───────────
 * Best-effort — never throws, never blocks. Emits a `console.warn`
 * for in-tab visibility AND beacons a single event per unique
 * (raw role, source) pair per session so we can track which new
 * vendor roles need an alias.
 */
const _seen = typeof Set !== 'undefined' ? new Set() : null;

export function unmappedRoleTelemetry(rawRole, source = 'unknown') {
  try {
    const key = `${source}::${rawRole}`;
    if (_seen && _seen.has(key)) return;
    if (_seen) _seen.add(key);
    // eslint-disable-next-line no-console
    console.warn(`[permissions] unmapped role "${rawRole}" from ${source}; falling back to ${UNKNOWN_ROLE_FALLBACK}`);
    if (typeof window !== 'undefined') {
      const apiBase = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) || '';
      const body = JSON.stringify({
        kind: 'unmapped_role',
        role: rawRole,
        source,
        ts: new Date().toISOString(),
      });
      const url = `${apiBase}/api/telemetry/frontend`;
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        try {
          const blob = new Blob([body], { type: 'application/json' });
          navigator.sendBeacon(url, blob);
          return;
        } catch { /* sendBeacon unavailable — fall through */ }
      }
      if (typeof fetch === 'function') {
        // Fire-and-forget; don't await, don't surface errors.
        try {
          fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true,
          }).catch(() => { /* swallow */ });
        } catch { /* swallow */ }
      }
    }
  } catch { /* never let telemetry break the app */ }
}

/* ─────────── Centralized resolver ───────────
 * `resolveRole(user, opts?)`
 *   user — caller's user object (any of: app_role, role, non_medical_role, category, permissions)
 *   opts.silent — when true, suppress telemetry for unknown roles (used by tests)
 *
 * Returns one of CANONICAL_ROLES.
 */
export function resolveRole(user, opts = {}) {
  if (!user || typeof user !== 'object') return UNKNOWN_ROLE_FALLBACK;
  const silent = !!opts.silent;

  const tryAlias = (raw, source) => {
    const v = ((raw == null ? '' : raw) + '').trim().toLowerCase();
    if (!v) return null;
    const aliased = ROLE_ALIASES[v];
    if (aliased) return aliased;
    if (!silent) unmappedRoleTelemetry(v, source);
    return UNKNOWN_ROLE_FALLBACK;
  };

  // 1 · app_role (per-org app role from /api/user-access/me)
  const fromApp = tryAlias(user.app_role, 'app_role');
  if (fromApp) return fromApp;

  // 2 · Team-context dispatch via vendor `user_type` (AriaOne §14).
  //     The vendor splits clinical role from operational role:
  //       - `non_medical` users carry the operational role on
  //         `non_medical_role` (front_desk / biller / office_manager …).
  //       - `medical_provider` users carry the clinical role on
  //         `member.role` (forwarded as `user.role` or `user.member_role`).
  //     We honor that split BEFORE falling through to the coarse
  //     `/auth/me.role` so a vendor team-context object resolves
  //     deterministically. Skipped silently when `user_type` is absent
  //     (legacy local users go straight to step 3).
  const userTypeRaw = ((user.user_type == null ? '' : user.user_type) + '').trim().toLowerCase();
  if (userTypeRaw) {
    const isNonMedical = (
      userTypeRaw === 'non_medical' ||
      userTypeRaw === 'non-medical' ||
      userTypeRaw === 'nonmedical'
    );
    const isMedical = (
      userTypeRaw === 'medical' ||
      userTypeRaw === 'medical_provider' ||
      userTypeRaw === 'medical-provider'
    );
    if (isNonMedical) {
      const fromNm = tryAlias(user.non_medical_role, 'user_type_non_medical');
      if (fromNm) return fromNm;
      // Vendor non-medical user with no operational role attached →
      // fall through to the token role (step 3) before giving up.
    } else if (isMedical) {
      const fromMember = tryAlias(user.member_role, 'user_type_member_role');
      if (fromMember) return fromMember;
      const fromRole = tryAlias(user.role, 'user_type_medical_role');
      if (fromRole) return fromRole;
      // Medical user with no role → default to provider (step 5 below).
      return 'provider';
    }
  }

  // 3 · token role from /api/auth/me
  const fromToken = tryAlias(user.role, 'role');
  if (fromToken) return fromToken;

  // 4 · team `non_medical_role` — operational role for non-medical
  //     team members (e.g. front_desk, office_manager, biller).
  const fromNonMedical = tryAlias(user.non_medical_role, 'non_medical_role');
  if (fromNonMedical) return fromNonMedical;

  // 5 · `category === 'medical'` → default to `provider` if no
  //     other role signal is available. Mirrors how the team
  //     adapter infers `category` from a vendor `user_type` of
  //     `medical_provider`.
  const cat = ((user.category || '') + '').trim().toLowerCase();
  if (cat === 'medical' || cat === 'medical_provider' || cat === 'medical-provider') {
    return 'provider';
  }

  // 6 · No usable signal → least-privilege fallback.
  return UNKNOWN_ROLE_FALLBACK;
}

/* Back-compat alias — older callsites import `roleFor`. */
export function roleFor(user) {
  return resolveRole(user);
}

export function can(roleOrUser, capability) {
  const role = typeof roleOrUser === 'string' ? roleOrUser : resolveRole(roleOrUser);
  return POLICY[capability]?.has(role) ?? false;
}

/**
 * Convenience: boolean capability shorthands for React components.
 *
 * AUTHORITATIVE SOURCE PRIORITY:
 *   1. `user.permissions[capability]` — computed server-side. If
 *      present, used verbatim and never second-guessed.
 *   2. `resolveRole(user)` + POLICY fallback — used only when the
 *      server hasn't yet rolled out the `permissions` field.
 */
export function capabilitiesFor(user) {
  const role = resolveRole(user);
  const serverPerms = user?.permissions || null;
  const capFromServer = (capKey) =>
    serverPerms && typeof serverPerms[capKey] === 'boolean'
      ? serverPerms[capKey]
      : undefined;
  const cap = (capKey) => {
    const s = capFromServer(capKey);
    return s !== undefined ? s : can(role, capKey);
  };
  return {
    role,
    canTeamAdmin:        cap('team:admin'),
    canSchedulerAdmin:   cap('scheduler:admin'),
    canSchedulerWrite:   cap('scheduler:ops:write'),
    canSchedulerRead:    cap('scheduler:ops:read'),
    canSchedulerSummary: cap('scheduler:summary:read'),
    // Traceability — UI tests assert the source of the decision.
    _source: serverPerms ? 'server' : 'local_role_map',
  };
}


/* ─────────── Optional server-side permissions probe ───────────
 *
 * `fetchAuthPermissionsSafe(fetcher?)`
 *
 * Vendor-hosted environments (ehi.ariaone.ai / login.ariaone.ai) do NOT
 * expose `/api/auth/permissions`. Local/dev deployments may or may not.
 * Either way, downstream scheduler/dashboard/team loaders MUST NEVER
 * block on this probe — a 404 or any other network failure is treated
 * as "endpoint unavailable, fall back to the local resolver".
 *
 * Semantics:
 *   - Returns the parsed JSON body on HTTP 200.
 *   - Returns `null` on 404 / 401 / 403 / any non-OK response.
 *   - Returns `null` on network error, missing global `fetch`, or
 *     missing `REACT_APP_BACKEND_URL`.
 *   - NEVER throws.
 *
 * `fetcher` (optional) — inject a custom fetch implementation for
 * testing; defaults to global `fetch`.
 */
export async function fetchAuthPermissionsSafe(fetcher) {
  try {
    const apiBase = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) || '';
    if (!apiBase) return null;
    const f = fetcher || (typeof fetch === 'function' ? fetch : null);
    if (!f) return null;
    const url = `${apiBase}/api/auth/permissions`;
    let res;
    try {
      res = await f(url, { method: 'GET', credentials: 'include' });
    } catch {
      // Network error / DNS / CORS — treat as unavailable.
      return null;
    }
    if (!res || !res.ok) {
      // 404 (vendor edge), 401/403 (unauthenticated probe) → optional.
      return null;
    }
    try {
      return await res.json();
    } catch {
      return null;
    }
  } catch {
    // Last-resort catch — never propagate.
    return null;
  }
}
